import { useState, useEffect } from 'react';
import { ApiService } from '../../services/api';
import { StatusBadge, LoadingSpinner, FileUpload } from './shared';
import { handleApiError, handleApiSuccess } from '../../utils/mtls-error-handler';
import { ProductEnvironmentPanel } from './ProductEnvironmentPanel';
import { formatDateTime } from '../../utils/date-utils';
import { ConfirmModal } from '../ConfirmModal';
import { useFileUpload } from '../../hooks/useFileUpload';

interface Org {
  id: number;
  name: string;
  email?: string | null;
  contactNumber?: string | null;
}

interface Certificate {
  id: number;
  commonName: string;
  status: string;
  certificateType?: string;
  expiryDate?: string;
  createdAt?: string;
}

interface Product {
  id: number;
  name: string;
  description?: string;
  environmentCount?: number;
}

interface Props {
  organization: Org;
  onClose: () => void;
}

export function OrganizationDetailPanel({ organization, onClose }: Props): JSX.Element {
  const [activeTab, setActiveTab] = useState<'certificates' | 'products'>('certificates');
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  useEffect(() => {
    loadTab();
  }, [activeTab, organization.id]);

  const loadTab = async () => {
    setLoading(true);
    try {
      if (activeTab === 'certificates') {
        const res = await ApiService.listMtlsOrganizationCertificates(organization.id);
        setCertificates(res.data || res.certificates || []);
      } else {
        const res = await ApiService.listMtlsOrganizationProducts(organization.id);
        setProducts(res.data || res.products || []);
      }
    } catch (e) {
      handleApiError(e, 'Organization Detail');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="modal-backdrop fade show"></div>
      <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
        <div className="modal-dialog modal-xl">
          <div className="modal-content">
            <div className="modal-header">
              <div>
                <h4 className="modal-title mb-0">
                  <i className="fas fa-building mr-2"></i>
                  {organization.name}
                </h4>
                {organization.email && (
                  <small className="text-muted">
                    <i className="fas fa-envelope mr-1"></i>{organization.email}
                    {organization.contactNumber && (
                      <span className="ml-3">
                        <i className="fas fa-phone mr-1"></i>{organization.contactNumber}
                      </span>
                    )}
                  </small>
                )}
              </div>
              <button type="button" className="close" onClick={onClose}>
                <span>&times;</span>
              </button>
            </div>

            {/* Tabs */}
            <div className="card-header p-0 pt-1" style={{ borderBottom: '1px solid #dee2e6' }}>
              <ul className="nav nav-tabs px-3">
                <li className="nav-item">
                  <a
                    className={`nav-link ${activeTab === 'certificates' ? 'active' : ''}`}
                    href="#"
                    onClick={e => { e.preventDefault(); setActiveTab('certificates'); }}
                  >
                    <i className="fas fa-certificate mr-2"></i>
                    Certificates
                    <span className="badge badge-info ml-2">{certificates.length}</span>
                  </a>
                </li>
                <li className="nav-item">
                  <a
                    className={`nav-link ${activeTab === 'products' ? 'active' : ''}`}
                    href="#"
                    onClick={e => { e.preventDefault(); setActiveTab('products'); }}
                  >
                    <i className="fas fa-cube mr-2"></i>
                    Products & Environments
                    <span className="badge badge-info ml-2">{products.length}</span>
                  </a>
                </li>
              </ul>
            </div>

            <div className="modal-body p-0">
              {loading ? (
                <div className="p-4 text-center"><LoadingSpinner text="Loading..." /></div>
              ) : activeTab === 'certificates' ? (
                <CertificatesTab
                  certificates={certificates}
                  organizationId={organization.id}
                  onReload={loadTab}
                />
              ) : (
                <ProductsTab
                  products={products}
                  organizationId={organization.id}
                  onSelectProduct={setSelectedProduct}
                  onReload={loadTab}
                />
              )}
            </div>

            <div className="modal-footer">
              <button className="btn btn-default" onClick={onClose}>Close</button>
            </div>
          </div>
        </div>
      </div>

      {/* Product environment panel — rendered on top */}
      {selectedProduct && (
        <ProductEnvironmentPanel
          product={selectedProduct}
          organizationId={organization.id}
          onClose={() => setSelectedProduct(null)}
        />
      )}
    </>
  );
}

// --- Certificates Tab ---
function CertificatesTab({
  certificates,
  organizationId,
  onReload,
}: {
  certificates: Certificate[];
  organizationId: number;
  onReload: () => void;
}) {
  const [deleting, setDeleting] = useState<number | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Certificate | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);

  const handleRevoke = (cert: Certificate) => {
    setConfirmDelete(cert);
  };

  const handleDeleteConfirm = async () => {
    if (!confirmDelete) return;
    setDeleting(confirmDelete.id);
    setConfirmDelete(null);
    try {
      await ApiService.deleteMtlsCertificate(confirmDelete.id);
      handleApiSuccess('Certificate deleted', 'Certificates');
      onReload();
    } catch (e) {
      handleApiError(e, 'Delete Certificate');
    } finally {
      setDeleting(null);
    }
  };

  if (certificates.length === 0) {
    return (
      <div className="p-4 text-center text-muted">
        <i className="fas fa-certificate fa-2x mb-2"></i>
        <p>No certificates for this organisation yet.</p>
        <button className="btn btn-sm btn-success" onClick={() => setShowAddModal(true)}>
          <i className="fas fa-upload mr-1"></i>Upload Certificate
        </button>
        {showAddModal && (
          <AddCertificateModal
            organizationId={organizationId}
            onClose={() => setShowAddModal(false)}
            onUploaded={() => { setShowAddModal(false); onReload(); }}
          />
        )}
      </div>
    );
  }

  return (
    <>
      <div className="p-3 border-bottom d-flex justify-content-end">
        <button className="btn btn-sm btn-success" onClick={() => setShowAddModal(true)}>
          <i className="fas fa-upload mr-1"></i>Upload Certificate
        </button>
      </div>
    <div className="table-responsive">
      <table className="table table-hover mb-0">
        <thead className="thead-light">
          <tr>
            <th>Common Name</th>
            <th>Type</th>
            <th>Status</th>
            <th>Expires</th>
            <th>Created</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {certificates.map(cert => (
            <tr key={cert.id}>
              <td><strong>{cert.commonName}</strong></td>
              <td>
                <span className={`badge ${cert.certificateType === 'prod' ? 'badge-danger' : 'badge-warning'}`}>
                  {cert.certificateType === 'prod' ? 'Production' : 'Non-Production'}
                </span>
              </td>
              <td><StatusBadge status={cert.status} type="certificate" /></td>
              <td className={cert.expiryDate && new Date(cert.expiryDate) < new Date(Date.now() + 30 * 86400000) ? 'text-danger' : ''}>
                {cert.expiryDate ? new Date(cert.expiryDate).toLocaleDateString() : '-'}
              </td>
              <td className="text-muted small">{cert.createdAt ? formatDateTime(cert.createdAt) : '-'}</td>
              <td>
                <button
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => handleRevoke(cert)}
                  disabled={deleting === cert.id}
                  title="Delete"
                >
                  {deleting === cert.id
                    ? <span className="spinner-border spinner-border-sm"></span>
                    : <i className="fas fa-trash"></i>}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

      {showAddModal && (
        <AddCertificateModal
          organizationId={organizationId}
          onClose={() => setShowAddModal(false)}
          onUploaded={() => { setShowAddModal(false); onReload(); }}
        />
      )}

      <ConfirmModal
        show={!!confirmDelete}
        title="Delete Certificate"
        message={`Delete certificate "${confirmDelete?.commonName}"? This cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmDelete(null)}
      />
    </>
  );
}

// --- Products Tab ---
function ProductsTab({
  products,
  organizationId,
  onSelectProduct,
  onReload,
}: {
  products: Product[];
  organizationId: number;
  onSelectProduct: (p: Product) => void;
  onReload: () => void;
}) {
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [unlinking, setUnlinking] = useState<number | null>(null);
  const [confirmUnlink, setConfirmUnlink] = useState<Product | null>(null);

  const handleUnlink = async () => {
    if (!confirmUnlink) return;
    setUnlinking(confirmUnlink.id);
    setConfirmUnlink(null);
    try {
      await ApiService.unlinkProductFromOrganization(organizationId, confirmUnlink.id);
      handleApiSuccess(`${confirmUnlink.name} unlinked`, 'Products');
      onReload();
    } catch (e) {
      handleApiError(e, 'Unlink Product');
    } finally {
      setUnlinking(null);
    }
  };

  return (
    <>
      <div className="p-3 border-bottom d-flex justify-content-end">
        <button className="btn btn-sm btn-success" onClick={() => setShowLinkModal(true)}>
          <i className="fas fa-link mr-1"></i>Link Product
        </button>
      </div>

      {products.length === 0 ? (
        <div className="p-4 text-center text-muted">
          <i className="fas fa-cube fa-2x mb-2"></i>
          <p>No products linked to this organisation yet.</p>
        </div>
      ) : (
        <div className="list-group list-group-flush">
          {products.map(product => (
            <div key={product.id} className="list-group-item d-flex align-items-center">
              <div className="flex-grow-1">
                <strong>{product.name}</strong>
                {product.description && (
                  <div className="text-muted small">{product.description}</div>
                )}
              </div>
              <div className="d-flex align-items-center">
                <span className="badge badge-info mr-3">
                  {product.environmentCount ?? 0} env{(product.environmentCount ?? 0) !== 1 ? 's' : ''}
                </span>
                <button
                  className="btn btn-sm btn-primary mr-2"
                  onClick={() => onSelectProduct(product)}
                >
                  <i className="fas fa-cogs mr-1"></i>Manage Environments
                </button>
                <button
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => setConfirmUnlink(product)}
                  disabled={unlinking === product.id}
                  title="Unlink product"
                >
                  {unlinking === product.id
                    ? <span className="spinner-border spinner-border-sm"></span>
                    : <i className="fas fa-unlink"></i>}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showLinkModal && (
        <LinkProductModal
          organizationId={organizationId}
          linkedProductIds={products.map(p => p.id)}
          onClose={() => setShowLinkModal(false)}
          onLinked={() => { setShowLinkModal(false); onReload(); }}
        />
      )}

      <ConfirmModal
        show={!!confirmUnlink}
        title="Unlink Product"
        message={`Remove "${confirmUnlink?.name}" from this organisation? This won't delete the product.`}
        confirmText="Unlink"
        variant="danger"
        onConfirm={handleUnlink}
        onCancel={() => setConfirmUnlink(null)}
      />
    </>
  );
}
function LinkProductModal({
  organizationId,
  linkedProductIds,
  onClose,
  onLinked,
}: {
  organizationId: number;
  linkedProductIds: number[];
  onClose: () => void;
  onLinked: () => void;
}) {
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [selectedId, setSelectedId] = useState<number | ''>('');
  const [loading, setLoading] = useState(true);
  const [linking, setLinking] = useState(false);

  useEffect(() => {
    ApiService.listMtlsProducts()
      .then(res => setAllProducts(res.data || res.products || []))
      .catch(e => handleApiError(e, 'Load Products'))
      .finally(() => setLoading(false));
  }, []);

  const available = allProducts.filter(p => !linkedProductIds.includes(p.id));

  const handleLink = async () => {
    if (!selectedId) return;
    setLinking(true);
    try {
      await ApiService.linkProductToOrganization(organizationId, selectedId as number);
      handleApiSuccess('Product linked', 'Products');
      onLinked();
    } catch (e) {
      handleApiError(e, 'Link Product');
    } finally {
      setLinking(false);
    }
  };

  return (
    <>
      <div className="modal-backdrop fade show" style={{ zIndex: 1060 }}></div>
      <div className="modal fade show" style={{ display: 'block', zIndex: 1070 }} tabIndex={-1}>
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title"><i className="fas fa-link mr-2"></i>Link Product</h5>
              <button type="button" className="close" onClick={onClose}><span>&times;</span></button>
            </div>
            <div className="modal-body">
              {loading ? (
                <LoadingSpinner text="Loading products..." />
              ) : available.length === 0 ? (
                <p className="text-muted">All available products are already linked.</p>
              ) : (
                <div className="form-group mb-0">
                  <label>Select Product</label>
                  <select
                    className="form-control"
                    value={selectedId}
                    onChange={e => setSelectedId(e.target.value ? Number(e.target.value) : '')}
                  >
                    <option value="">-- choose a product --</option>
                    {available.map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-default" onClick={onClose}>Cancel</button>
              <button
                className="btn btn-success"
                onClick={handleLink}
                disabled={!selectedId || linking || loading}
              >
                {linking ? <span className="spinner-border spinner-border-sm mr-1"></span> : null}
                Link Product
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// --- Add Certificate Modal ---
function AddCertificateModal({
  organizationId,
  onClose,
  onUploaded,
}: {
  organizationId: number;
  onClose: () => void;
  onUploaded: () => void;
}) {
  const { uploadState, uploadCertificate } = useFileUpload();
  const [certType, setCertType] = useState<'prod' | 'non-prod'>('non-prod');
  const [description, setDescription] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleSubmit = async () => {
    if (!selectedFile) return;
    const success = await uploadCertificate(organizationId, selectedFile, description, certType);
    if (success) onUploaded();
  };

  return (
    <>
      <div className="modal-backdrop fade show" style={{ zIndex: 1060 }}></div>
      <div className="modal fade show" style={{ display: 'block', zIndex: 1070 }} tabIndex={-1}>
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title"><i className="fas fa-upload mr-2"></i>Upload Certificate</h5>
              <button type="button" className="close" onClick={onClose} disabled={uploadState.uploading}>
                <span>&times;</span>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Certificate Type</label>
                <select
                  className="form-control"
                  value={certType}
                  onChange={e => setCertType(e.target.value as 'prod' | 'non-prod')}
                  disabled={uploadState.uploading}
                >
                  <option value="non-prod">Non-Production</option>
                  <option value="prod">Production</option>
                </select>
                <small className="form-text text-muted">
                  {certType === 'prod'
                    ? 'Production certificates can only be assigned to production environments'
                    : 'Non-production certificates can be assigned to dev, test, uat, and preprod environments'}
                </small>
              </div>
              <div className="form-group">
                <label>Description (Optional)</label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  disabled={uploadState.uploading}
                  placeholder="Enter a description..."
                />
              </div>
              <div className="form-group mb-0">
                <label>Certificate File (.pem, .crt, .cer)</label>
                <FileUpload
                  onFileSelect={setSelectedFile}
                  acceptedTypes={['.pem', '.crt', '.cer', '.p12', '.pfx']}
                  maxSize={10 * 1024 * 1024}
                  loading={uploadState.uploading}
                  error={uploadState.error}
                />
              </div>
              {uploadState.uploading && (
                <div className="progress mt-3">
                  <div
                    className="progress-bar progress-bar-striped progress-bar-animated"
                    style={{ width: `${uploadState.progress}%` }}
                  >
                    {uploadState.progress}%
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-default" onClick={onClose} disabled={uploadState.uploading}>Cancel</button>
              <button
                className="btn btn-success"
                onClick={handleSubmit}
                disabled={!selectedFile || uploadState.uploading}
              >
                {uploadState.uploading
                  ? <><span className="spinner-border spinner-border-sm mr-1"></span>Uploading...</>
                  : 'Upload Certificate'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
